import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '../../../logging/logging.ts';
import { Tkv2PDF, Tkv2PDFChartImages } from '../../tkv2PDF/types.ts';
import { generateTkv2PDFChartImages } from '../../tkv2PDF/utils/generateTkv2PDFChartImages.ts';

export type PdfPreviewSnapshot = {
  data: Tkv2PDF;
  chartImages: Tkv2PDFChartImages;
  includeCostApproach: boolean;
  generation: number;
};

export const usePdfPreviewSnapshot = (
  liveData: Tkv2PDF,
  includeCostApproach: boolean,
  isReady = true,
) => {
  const [snapshot, setSnapshot] = useState<PdfPreviewSnapshot>();
  const [isRefreshing, setIsRefreshing] = useState(true);

  const liveDataRef = useRef(liveData);
  const includeCostApproachRef = useRef(includeCostApproach);
  const isReadyRef = useRef(isReady);
  const requestIdRef = useRef(0);

  liveDataRef.current = liveData;
  includeCostApproachRef.current = includeCostApproach;
  isReadyRef.current = isReady;

  const refresh = useCallback(() => {
    if (!isReadyRef.current) {
      setIsRefreshing(true);
      return;
    }

    const requestId = ++requestIdRef.current;
    const data = liveDataRef.current;
    const includeCostApproachAtRefresh = includeCostApproachRef.current;
    setIsRefreshing(true);

    void generateTkv2PDFChartImages(
      data.chartParams,
      includeCostApproachAtRefresh,
      data.marketAggregateData,
    )
      .then((chartImages) => {
        if (requestId !== requestIdRef.current) return;
        if (!isReadyRef.current) return;
        setSnapshot({
          data,
          chartImages,
          includeCostApproach: includeCostApproachAtRefresh,
          generation: requestId,
        });
      })
      .catch((error: unknown) => {
        if (requestId !== requestIdRef.current) return;
        logger().error(error, 'pdf-preview-chart-generation');
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        if (!isReadyRef.current) return;
        setIsRefreshing(false);
      });
  }, []);

  useEffect(() => {
    if (snapshot) return;
    refresh();
  }, [refresh, liveData, includeCostApproach, isReady, snapshot]);

  const isStale =
    snapshot == null ||
    !isEqual(liveData, snapshot.data) ||
    includeCostApproach !== snapshot.includeCostApproach;

  return { snapshot, isStale, isRefreshing, refresh };
};
